import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../contexts/PopupContext';
import { supabase } from '../../lib/supabase';
import { getFriends } from '../../utils/friendsService';
import {
  applyDiscoverEventFilters,
  defaultMapDiscoverFilters,
  dismissDiscoverFilterChip,
  mapDiscoverFiltersActive,
} from '../../utils/discoverEventFilters';
import { buildMapActiveFilterChips } from '../../utils/mapActiveFilterChips';
import EventCard from '../discover/EventCard';
import MapActiveFilterChips from '../discover/map/MapActiveFilterChips';
import Loading from '../layout/Loading';
import EventEmptyStateIcon from './EventEmptyStateIcon';
import './PendingView.css';

function PendingView({ refreshKey = 0, onSwitchToUpcoming, isActive = true }) {
  const { t } = useTranslation(['events', 'discover']);
  const { openPopup } = usePopup();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(defaultMapDiscoverFilters());
  const [currentUserId, setCurrentUserId] = useState(null);
  const [friendIdSet, setFriendIdSet] = useState(new Set());
  const channelRef = useRef(null);
  const refreshKeyRef = useRef(refreshKey);
  refreshKeyRef.current = refreshKey;
  const loadedRefreshKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;
      setCurrentUserId(user?.id ?? null);
      if (!user?.id) {
        setFriendIdSet(new Set());
        return;
      }
      const fr = await getFriends(user.id);
      if (cancelled) return;
      if (fr.success && Array.isArray(fr.data)) {
        setFriendIdSet(new Set(fr.data.map((f) => f.id)));
      } else {
        setFriendIdSet(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPendingEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const today = new Date().toISOString().split('T')[0];

      // Load events where user has pending applications
      let applicationsQuery = supabase
        .from('event_applications')
        .select('event_id, events!inner (*)')
        .eq('user_id', user.id)
        .eq('status', 'pending');

      // Try to add is_cancelled filter, but don't fail if column doesn't exist
      try {
        applicationsQuery = applicationsQuery.eq('events.is_cancelled', false);
      } catch (err) {
        // Column doesn't exist yet, continue without filter
      }

      let { data: pendingApplications, error: applicationsError } = await applicationsQuery;

      // If error is about missing column, retry without filter
      if (applicationsError && applicationsError.message && applicationsError.message.includes('is_cancelled')) {
        const retryResult = await supabase
          .from('event_applications')
          .select('event_id, events!inner (*)')
          .eq('user_id', user.id)
          .eq('status', 'pending');
        pendingApplications = retryResult.data;
        applicationsError = retryResult.error;
      }

      if (applicationsError) throw applicationsError;

      // Get all events where user is already an ACTIVE participant
      // to exclude them from pending list
      // Note: If user left (withdrawn) or was kicked, they can apply again
      const { data: participantEvents, error: participantError } = await supabase
        .from('event_participants')
        .select('event_id')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (participantError) throw participantError;

      // Create a set of event IDs where user is already an active participant
      const activeParticipantEventIds = new Set();
      if (participantEvents) {
        participantEvents.forEach(({ event_id }) => {
          activeParticipantEventIds.add(event_id);
        });
      }

      // Filter and process events
      const pendingEvents = [];
      if (pendingApplications) {
        pendingApplications.forEach(({ events: event, event_id }) => {
          // Only include upcoming events that user is NOT already an active participant in
          // If user left (withdrawn) or was kicked (kicked), they can still have pending applications
          // Exclude cancelled events (if is_cancelled field exists)
          if (event && 
              event.is_cancelled !== true &&
              event.event_date >= today && 
              !activeParticipantEventIds.has(event_id)) {
            pendingEvents.push(event);
          }
        });
      }

      // Sort by date and time
      pendingEvents.sort((a, b) => {
        const dateA = new Date(`${a.event_date}T${a.start_time}`);
        const dateB = new Date(`${b.event_date}T${b.start_time}`);
        return dateA - dateB;
      });

      setEvents(pendingEvents);
      loadedRefreshKeyRef.current = refreshKeyRef.current;
    } catch (err) {
      console.error('Error loading pending events:', err);
      setError(err.message || t('events.pending.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const applyFiltersToEvents = useCallback((eventsList) => {
    const filtered = applyDiscoverEventFilters(eventsList, filters, {
      friendIds: friendIdSet,
      currentUserId,
    });
    setFilteredEvents(filtered);
  }, [filters, friendIdSet, currentUserId]);

  // Apply filters when events or filters change
  useEffect(() => {
    if (events.length > 0) {
      applyFiltersToEvents(events);
    } else {
      setFilteredEvents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, filters]);

  useEffect(() => {
    if (!isActive) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return undefined;
    }

    if (loadedRefreshKeyRef.current !== refreshKey) {
      loadPendingEvents();
    }

    let cancelled = false;

    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }

      const channel = supabase
        .channel(`pending-applications-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'event_applications',
            filter: `user_id=eq.${user.id}`
          },
          async (payload) => {
            console.log('Application change detected:', payload);

            if (payload.eventType === 'UPDATE') {
              const oldStatus = payload.old?.status;
              const newStatus = payload.new?.status;

              if (oldStatus === 'pending' && newStatus === 'approved') {
                setTimeout(() => {
                  if (onSwitchToUpcoming) {
                    onSwitchToUpcoming();
                  }
                }, 500);
              }
            }

            setTimeout(() => {
              loadPendingEvents();
            }, 300);
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'event_participants',
            filter: `user_id=eq.${user.id}`
          },
          async (payload) => {
            console.log('Participant change detected:', payload);

            if (payload.eventType === 'INSERT') {
              setTimeout(() => {
                if (onSwitchToUpcoming) {
                  onSwitchToUpcoming();
                }
              }, 200);
            }

            loadPendingEvents();
          }
        )
        .subscribe();

      if (!cancelled) {
        channelRef.current = channel;
      } else {
        supabase.removeChannel(channel);
      }
    };

    setupRealtimeSubscription();

    return () => {
      cancelled = true;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isActive, refreshKey, loadPendingEvents, onSwitchToUpcoming]);


  if (loading) {
    return (
      <div className="pending-view">
        <Loading 
          className="pending-view-loading" 
          size="medium"
          message={t('events.pending.loading')}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pending-view">
        <div className="pending-view-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const handleOpenFilters = () => {
    openPopup('event-filters', {
      initialFilters: filters,
      onApply: (newFilters) => {
        setFilters(newFilters);
      }
    }, 0);
  };

  const hasActiveFilters = () => {
    return mapDiscoverFiltersActive(filters);
  };

  const filtersActive = hasActiveFilters();
  const displayEvents = filtersActive ? filteredEvents : events;
  const displayCount = filtersActive ? filteredEvents.length : events.length;
  const activeFilterChips = buildMapActiveFilterChips(
    { searchCenter: null, searchAddress: '', searchRange: '', mapDiscoverFilters: filters },
    t
  );

  const handleDismissFilterChip = (chipId) => {
    setFilters((prev) => dismissDiscoverFilterChip(prev, chipId));
  };

  if (events.length === 0) {
    return (
      <div className="pending-view">
        <div className="pending-view-empty pending-view-empty-card">
          <div className="pending-view-empty-icon" aria-hidden="true">
            <EventEmptyStateIcon variant="pending" />
          </div>
          <h2>{t('events.pending.title')}</h2>
          <p>{t('events.pending.description')}</p>
          <span className="pending-view-empty-tip">
            {t('events.pending.emptyTip', 'Twoje zgłoszenia pojawią się tutaj do momentu decyzji organizatora.')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="pending-view">
      <div className="pending-view-header">
        <div className="pending-view-header-left">
          <h2>{t('events.pending.title')}</h2>
          <p className="pending-view-count">
            {displayCount} {t('events.pending.eventsCount')}
            {hasActiveFilters() && events.length !== displayCount && (
              <span className="pending-view-filters-active">
                {' '}({t('events.filters.filtered', 'przefiltrowane')} z {events.length})
              </span>
            )}
          </p>
          {hasActiveFilters() && (
            <span className="pending-view-filters-active-badge">
              {t('events.filters.active', 'Filtry aktywne')}
            </span>
          )}
        </div>
        <button
          className="filter-button"
          onClick={handleOpenFilters}
          title={t('events.filters.filter', 'Filtruj')}
        >
          <svg className="filter-button-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <path d="M4 6h16M8 12h8M10 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="6" cy="6" r="1.75" fill="currentColor" />
            <circle cx="16" cy="12" r="1.75" fill="currentColor" />
            <circle cx="14" cy="18" r="1.75" fill="currentColor" />
          </svg>
          {t('events.filters.filter', 'Filtruj')}
          {hasActiveFilters() && <span className="filter-badge">•</span>}
        </button>
      </div>
      {filtersActive && (
        <MapActiveFilterChips chips={activeFilterChips} onDismiss={handleDismissFilterChip} />
      )}
      <div className="pending-view-events">
        {displayEvents.length > 0 ? (
          displayEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))
        ) : (
          <div className="pending-view-empty">
            <p>{t('events.filters.noResults', 'Brak wydarzeń spełniających wybrane kryteria')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PendingView;
